import { useEffect, useMemo, useState } from "react";
import { MIN_LEFTOVER_INCHES } from "../static/pipe";
import { formatSizeLabel } from "../lib/utils";
import {
  ItemOptionProps,
  MaterialOptionProps,
  MaterialSizeOptionProps,
  ProcessOrderLeftoverPoolProps,
  ProcessOrderMaterialGroupsPayload,
} from "../types/processOrder";

type CuttingWatchedItem = {
  item_id?: number | string;
  item_size_id?: number | string;
  cutting_size?: number | string;
  size_id?: number | string;
  qty?: number | string;
};

// Client-side predictor of ProcessOrdersController::buildCuttingOrderItems() —
// same grouping, same largest-first leftover allocation, same fresh-pipe cutting.
// Keep the two in lockstep; a mismatch shows up as "the preview said 2 pipes,
// the saved order says 3" with nothing to point at why.
export type MaterialGroupCalc = {
  sizeId: number;
  sizeName: string;
  pipeLengthInch: number;
  itemDetails: {
    index: number;
    itemName: string;
    cuttingSizeMm: number;
    qty: number;
    piecesFromLeftover: number;
    freshPipes: number;
    freshPipeLeftover: number;
    runningLeftoverAfter: number;
  }[];
  totalFreshPipes: number;
  finalLeftover: number;
  savedPieces: number[];
};

type UseCuttingMaterialGroupsParams = {
  // False for every non-cutting process. The hook still runs (React hook
  // order must stay stable) but returns empty/no-op results.
  enabled: boolean;
  watchedItems: CuttingWatchedItem[] | undefined;
  itemOptions: ItemOptionProps[];
  // Material Size is still the single value grouping/storage keys off
  // (size_id, unchanged). materialOptions is only needed to resolve the
  // material's name for the "Material (Size)" chip label.
  materialOptions: MaterialOptionProps[];
  materialSizeOptions: MaterialSizeOptionProps[];
  dbLeftovers: ProcessOrderLeftoverPoolProps[];
};

export function useCuttingMaterialGroups({
  enabled,
  watchedItems,
  itemOptions,
  materialOptions,
  materialSizeOptions,
  dbLeftovers,
}: UseCuttingMaterialGroupsParams) {
  const [groupSelectedLeftovers, setGroupSelectedLeftovers] = useState<Record<string, number[]>>({});

  const sizeIdsKey = watchedItems?.map((i) => String(i?.size_id || 0)).join(",") ?? "";
  const watchedItemsKey =
    watchedItems?.map((i) => `${i?.item_id}-${i?.item_size_id}-${i?.cutting_size}-${i?.size_id}-${i?.qty}`).join(",") ?? "";

  // Drop leftover selections for groups that no longer exist (item removed /
  // size changed).
  useEffect(() => {
    if (!enabled || !watchedItems) return;

    const currentSizeIds = new Set<string>();
    watchedItems.forEach((item) => {
      const sizeId = String(item?.size_id || 0);
      if (sizeId !== "0") currentSizeIds.add(sizeId);
    });

    setGroupSelectedLeftovers((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => { if (!currentSizeIds.has(k)) delete next[k]; });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, sizeIdsKey]);

  const materialGroups = useMemo((): MaterialGroupCalc[] => {
    if (!enabled || !watchedItems) return [];

    const grouped: Record<string, { items: CuttingWatchedItem[]; indices: number[] }> = {};
    watchedItems.forEach((item, index) => {
      const sizeId = String(item?.size_id || 0);
      if (sizeId === "0") return;
      if (!grouped[sizeId]) grouped[sizeId] = { items: [], indices: [] };
      grouped[sizeId].items.push(item);
      grouped[sizeId].indices.push(index);
    });

    return Object.entries(grouped).map(([sizeId, group]) => {
      const sizeOpt = materialSizeOptions.find((s) => s.value === Number(sizeId));
      const materialOpt = materialOptions.find((m) => m.value === sizeOpt?.material_id);
      // A material's own size IS its raw-material stock length for cutting —
      // there is no separate pipe size to pick (mirrors
      // ProcessOrdersController::buildCuttingOrderItems()).
      const pipeLengthInch = sizeOpt?.size || 3660;

      const pieces: { remaining: number; source: "db" | "fresh" }[] = [];

      const dbIds = groupSelectedLeftovers[sizeId] || [];
      for (const loId of dbIds) {
        const lo = dbLeftovers.find((l) => l.id === loId);
        if (lo) pieces.push({ remaining: lo.remaining_inches, source: "db" });
      }

      const sumPool = () => Math.round(pieces.reduce((s, p) => s + p.remaining, 0) * 1000) / 1000;

      let totalFreshPipes = 0;
      const itemDetails = group.items.map((item, gi) => {
        const itemOpt = itemOptions.find((o) => o.value === Number(item.item_id));
        const cuttingSizeMm = Number(item.cutting_size) || 0;
        const qty = Number(item.qty) || 0;

        if (qty <= 0 || cuttingSizeMm <= 0) {
          return {
            index: group.indices[gi],
            itemName: itemOpt?.label || "—",
            cuttingSizeMm,
            qty,
            piecesFromLeftover: 0,
            freshPipes: 0,
            freshPipeLeftover: 0,
            runningLeftoverAfter: sumPool(),
          };
        }

        const piecesPerPipe = Math.floor(pipeLengthInch / cuttingSizeMm);
        if (piecesPerPipe <= 0) {
          return {
            index: group.indices[gi],
            itemName: itemOpt?.label || "—",
            cuttingSizeMm,
            qty,
            piecesFromLeftover: 0,
            freshPipes: 0,
            freshPipeLeftover: 0,
            runningLeftoverAfter: sumPool(),
          };
        }

        let remaining = qty;
        let piecesFromLeftover = 0;
        const sortedIdx = pieces
          .map((_, i) => i)
          .sort((a, b) => pieces[b].remaining - pieces[a].remaining);
        for (const idx of sortedIdx) {
          if (remaining === 0) break;
          const canFit = Math.floor(pieces[idx].remaining / cuttingSizeMm);
          if (canFit <= 0) continue;
          const take = Math.min(canFit, remaining);
          pieces[idx].remaining = Math.round((pieces[idx].remaining - take * cuttingSizeMm) * 1000) / 1000;
          remaining -= take;
          piecesFromLeftover += take;
        }

        let freshPipes = 0;
        let lastFreshPipeLeftover = 0;
        while (remaining > 0) {
          const cut = Math.min(piecesPerPipe, remaining);
          const leftover = Math.round((pipeLengthInch - cut * cuttingSizeMm) * 1000) / 1000;
          pieces.push({ remaining: leftover, source: "fresh" });
          remaining -= cut;
          freshPipes += 1;
          lastFreshPipeLeftover = leftover;
        }

        totalFreshPipes += freshPipes;

        return {
          index: group.indices[gi],
          itemName: itemOpt?.label || "—",
          cuttingSizeMm,
          qty,
          piecesFromLeftover,
          freshPipes,
          freshPipeLeftover: lastFreshPipeLeftover,
          runningLeftoverAfter: sumPool(),
        };
      });

      const savedPieces = pieces
        .filter((p) => p.remaining >= MIN_LEFTOVER_INCHES)
        .map((p) => Math.round(p.remaining * 1000) / 1000);
      const finalLeftover = Math.round(savedPieces.reduce((s, v) => s + v, 0) * 1000) / 1000;

      return {
        sizeId: Number(sizeId),
        sizeName: sizeOpt
          ? formatSizeLabel({ name: sizeOpt.label, material_name: materialOpt?.label })
          : sizeId,
        pipeLengthInch,
        itemDetails,
        totalFreshPipes,
        finalLeftover,
        savedPieces,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enabled,
    watchedItemsKey,
    JSON.stringify(groupSelectedLeftovers),
    materialSizeOptions, materialOptions, itemOptions, dbLeftovers,
  ]);

  const getAvailableLeftoversForGroup = (sizeId: string): ProcessOrderLeftoverPoolProps[] => {
    const numSizeId = Number(sizeId);
    if (!numSizeId) return [];
    const usedByOthers = new Set<number>();
    Object.entries(groupSelectedLeftovers).forEach(([gSizeId, ids]) => {
      if (gSizeId !== sizeId) ids.forEach((id) => usedByOthers.add(id));
    });
    return dbLeftovers.filter((l) => l.size_id === numSizeId && !usedByOthers.has(l.id));
  };

  const toggleGroupLeftover = (sizeId: string, leftoverId: number) => {
    setGroupSelectedLeftovers((prev) => {
      const current = prev[sizeId] || [];
      const exists = current.includes(leftoverId);
      return {
        ...prev,
        [sizeId]: exists ? current.filter((id) => id !== leftoverId) : [...current, leftoverId],
      };
    });
  };

  const buildMaterialGroupsPayload = (): ProcessOrderMaterialGroupsPayload => {
    const payload: ProcessOrderMaterialGroupsPayload = {};
    materialGroups.forEach((mg) => {
      const sizeId = String(mg.sizeId);
      payload[sizeId] = { use_leftover_ids: groupSelectedLeftovers[sizeId] || [] };
    });
    return payload;
  };

  return {
    materialGroups,
    groupSelectedLeftovers,
    getAvailableLeftoversForGroup,
    toggleGroupLeftover,
    buildMaterialGroupsPayload,
  };
}
